import React, { useEffect } from 'react';
import { TreeTable, TreeTableSortEvent } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { useState, useCallback, useMemo } from 'react';
import { TextOverflowTooltip } from 'src/common';
import { Header } from './comps/Header';
import { AccessAvatar, SortIcon, Tdm } from './comps';
import { GTColumnSelectAnchor } from 'src/components/GenericTable/comps';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { get } from 'lodash';
import { ColumnHeader, Container, TableContainer } from './styled';
import { TableIds } from 'src/api/types/misc';
import { formatDate } from 'src/utils/misc';
import { getMemberAccessLabel, getRoleLabel } from 'src/api/types';
import { useAuth } from 'src/state/auth';
import { buildTreeFromFlat, convertToTreeTableFormat, FlatOrgData, FlatOrgTreeTableNode } from 'src/utils/treeNode';

interface AccessTableProps {
  data: FlatOrgData[];
  selectedId?: string;
  onRowSelect?: (selected: FlatOrgData | null) => void;
  isOwnProfile: boolean;
  hasUnitSelected: boolean;
  onViewGlobal: () => void;
  closeModal: () => void;
  isRowDisabled?: (item: FlatOrgData) => boolean;
}

const AccessTable: React.FC<AccessTableProps> = ({
  data,
  selectedId,
  onRowSelect,
  isOwnProfile,
  hasUnitSelected,
  onViewGlobal,
  closeModal,
  isRowDisabled,
}) => {
  const { profile, isPlatformAdmin } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useLocalStorage<string>(`${TableIds.accessTable}-sort-field`, '');
  const [sortOrder, setSortOrder] = useLocalStorage<0 | 1 | -1 | null | undefined>(
    `${TableIds.accessTable}-sort-order`,
    1,
  );
  const [visibleColumns, setVisibleColumns] = useLocalStorage<string[]>(`${TableIds.accessTable}-visible-columns`, [
    'account-type',
    'user-name',
    'join-date',
    'role',
    'access',
    'notifications',
  ]);
  const treeData = useMemo(() => buildTreeFromFlat(data, undefined, false), [data]);
  const treeTableData = useMemo(() => convertToTreeTableFormat(treeData), [treeData]);

  const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (treeTableData.length > 0) {
      setExpandedKeys({ [treeTableData[0].key]: true });
    }
  }, [treeTableData]);

  const customSortFunction = useCallback(
    (data: FlatOrgTreeTableNode[]): FlatOrgTreeTableNode[] => {
      return [...data].sort((a, b) => {
        if (a.data.recordType !== b.data.recordType) {
          return a.data.recordType === 'W' ? -1 : 1;
        }

        if (sortField && sortOrder !== 0 && sortOrder !== null && sortOrder !== undefined) {
          let valueA = get(a.data, sortField);
          let valueB = get(b.data, sortField);

          if (valueA === undefined) return 1;
          if (valueB === undefined) return -1;

          if (typeof valueA === 'string' && typeof valueB === 'string') {
            return sortOrder * valueA.localeCompare(valueB);
          }

          return sortOrder * (valueA - valueB);
        }

        return 0;
      });
    },
    [sortField, sortOrder],
  );

  const applyCustomSort = useCallback(
    (dataArray: FlatOrgTreeTableNode[]): FlatOrgTreeTableNode[] => {
      const sortedData = customSortFunction(dataArray);

      return sortedData.map((node) => {
        if (node.children && node.children.length > 0) {
          return { ...node, children: applyCustomSort(node.children) };
        }
        return node;
      });
    },
    [customSortFunction],
  );

  const filterData = useCallback((data: FlatOrgTreeTableNode[], term: string): FlatOrgTreeTableNode[] => {
    if (!term.trim()) return JSON.parse(JSON.stringify(data));

    return data
      .filter((node) => {
        const matchesSearch =
          node.data.recordType === 'O' &&
          (node.data.name.toLowerCase().includes(term.toLowerCase()) ||
            node.data.domain.toLowerCase().includes(term.toLowerCase()) ||
            node.data.id.toLowerCase().includes(term.toLowerCase()) ||
            node.data.primaryContact.toLowerCase().includes(term.toLowerCase()));

        if (matchesSearch) return true;

        if (node.children && node.children.length > 0) {
          const filteredChildren = filterData(node.children, term);
          if (filteredChildren.length) {
            return true;
          }
        }

        return false;
      })
      .map((node) => {
        if (node.children && node.children.length > 0) {
          if (
            node.data.recordType === 'O' &&
            (node.data.name.toLowerCase().includes(term.toLowerCase()) ||
              node.data.domain.toLowerCase().includes(term.toLowerCase()) ||
              node.data.id.toLowerCase().includes(term.toLowerCase()) ||
              node.data.primaryContact.toLowerCase().includes(term.toLowerCase()))
          ) {
            return {
              ...node,
              children: JSON.parse(JSON.stringify(node.children)),
            };
          }

          const filteredChildren = filterData(node.children, term);
          return {
            ...node,
            children: filteredChildren,
          };
        }
        return node;
      });
  }, []);

  const onSort = (event: TreeTableSortEvent) => {
    if (sortField === event.sortField) {
      if (sortOrder === 1) {
        setSortOrder(-1);
      } else if (sortOrder === -1) {
        setSortField('');
        setSortOrder(0);
      } else {
        setSortField(event.sortField);
        setSortOrder(1);
      }
    } else {
      setSortField(event.sortField);
      setSortOrder(1);
    }
  };

  const filteredData = useMemo(() => {
    const filtered = filterData(treeTableData, searchTerm);
    return applyCustomSort(filtered);
  }, [filterData, treeTableData, searchTerm, applyCustomSort]);

  const columnSelectAnchor = useMemo(
    () => (
      <GTColumnSelectAnchor
        options={[
          { key: 'account-type', title: 'Account Type' },
          { key: 'user-name', title: 'User Name' },
          { key: 'join-date', title: 'Join Date' },
          { key: 'role', title: 'Role' },
          { key: 'access', title: 'Access' },
          { key: 'notifications', title: 'Notifications' },
        ]}
        value={visibleColumns}
        defaultOptions={['account-type', 'user-name', 'join-date', 'role', 'access', 'notifications']}
        onChange={setVisibleColumns}
        tableId={TableIds.accessTable}
      />
    ),
    [visibleColumns, setVisibleColumns],
  );

  const getColumnClassName = (column: string) => {
    return visibleColumns.includes(column) ? '' : 'c-hidden';
  };

  return (
    <Container>
      <TableContainer>
        <TreeTable
          value={filteredData}
          header={
            <Header
              onChange={setSearchTerm}
              showViewGlobal={isOwnProfile && hasUnitSelected}
              onViewGlobal={onViewGlobal}
            />
          }
          onSort={onSort}
          filterMode="strict"
          sortOrder={sortOrder}
          expandedKeys={expandedKeys}
          onToggle={(e) => setExpandedKeys(e.value)}
          rowClassName={(node) => {
            const isSelected = node.data.id === selectedId;
            const isDisabled = isRowDisabled?.(node.data) ?? false;
            return {
              expandable: node.children?.length ? true : false,
              'selected-row': isSelected,
              'disabled-row': isDisabled,
            };
          }}
          onRowClick={(e) => {
            const nodeData = e.node.data;
            const isDisabled = isRowDisabled?.(nodeData) ?? false;

            if (isDisabled) {
              return;
            }

            const target = e.originalEvent?.target as HTMLElement;
            const isTogglerClick =
              target?.classList.contains('p-treetable-toggler') || target?.closest('.p-treetable-toggler') !== null;

            if (!isTogglerClick) {
              onRowSelect?.(nodeData);
            }
          }}
          sortIcon={() => null}
        >
          {/* Name (O,W) */}
          <Column
            field="name"
            header={
              <ColumnHeader>
                <span>Name</span>
                <SortIcon sortOrder={sortField === 'name' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            expander
            sortable
            className="c-name"
            body={({ data }) => {
              return <AccessAvatar name={data.name} imgUrl={data.imgUrl} gradientBlue={data.recordType === 'W'} />;
            }}
          />

          {/* Account Type, (O,W) */}
          <Column
            field="account-type"
            header={
              <ColumnHeader>
                <span>Account Type</span>
                <SortIcon sortOrder={sortField === 'account-type' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('account-type')}
            body={({ data }) => {
              const archivedLabel = data.isArchived ? ' (Archived)' : '';
              return (
                <TextOverflowTooltip>
                  {data.recordType === 'O' ? `Organisation${archivedLabel}` : `Workspace${archivedLabel}`}
                </TextOverflowTooltip>
              );
            }}
          />

          {/* User Name */}
          <Column
            field="user-name"
            header={
              <ColumnHeader>
                <span>User Name</span>
                <SortIcon sortOrder={sortField === 'user-name' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('user-name')}
            body={({ data }) => <TextOverflowTooltip>{data.member?.association?.userName || '-'}</TextOverflowTooltip>}
          />

          {/* Join Date */}
          <Column
            field="join-date"
            header={
              <ColumnHeader>
                <span>Join Date</span>
                <SortIcon sortOrder={sortField === 'join-date' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('join-date')}
            body={({ data }) => {
              if (data.member?.association?.acceptedDate === null) return <span>-</span>;
              return (
                <TextOverflowTooltip>{formatDate(data.member?.association?.acceptedDate) || '-'}</TextOverflowTooltip>
              );
            }}
          />

          {/* Role */}
          <Column
            field="role"
            header={
              <ColumnHeader>
                <span>Role</span>
                <SortIcon sortOrder={sortField === 'role' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('role')}
            body={({ data }) => (
              <TextOverflowTooltip>{getRoleLabel(data.member?.effectiveRole) || '-'}</TextOverflowTooltip>
            )}
          />

          {/* Access */}
          <Column
            field="access"
            header={
              <ColumnHeader>
                <span>Access</span>
                <SortIcon sortOrder={sortField === 'access' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('access')}
            body={({ data }) => {
              return <TextOverflowTooltip>{getMemberAccessLabel(data.member?.access)}</TextOverflowTooltip>;
            }}
          />

          <Column
            field="notifications"
            header={
              <ColumnHeader>
                <span>Notifications</span>
                <SortIcon sortOrder={sortField === 'notifications' ? sortOrder || -1 : 0} />
              </ColumnHeader>
            }
            sortable
            className={getColumnClassName('notifications')}
            body={({ data }) => {
              if (data.recordType === 'W') {
                return <span>{data.member?.association?.notifications ? 'Yes' : 'No'}</span>;
              }
              return null;
            }}
          />

          {/* TDM */}
          <Column
            field=""
            header={columnSelectAnchor}
            className="c-tdm"
            body={({ data }, { rowIndex }) => <Tdm data={data} rowIndex={rowIndex} closeModal={closeModal} />}
          />
        </TreeTable>
      </TableContainer>
    </Container>
  );
};

export default AccessTable;
